import { useState } from 'react'
import { router, Link, useForm } from '@inertiajs/react'
import Layout from '../../components/Layout'
import KanbanBoard from './_Kanban/Board'
import ToDoDetail from './_Kanban/ToDoDetail'
import { sortPeople } from '../../utils/peopleSort'

interface User { id: number; full_name: string; avatar_text: string | null }
interface ToDo {
  id: number; title: string; description: string | null;
  priority: string; due_date: string | null; position: number;
  to_do_list_id: number; owner: User | null; completed: boolean;
  checklist: Array<{ id?: string; title: string; done: boolean }>;
  linked_action_item_id: number | null; linked_topic_id: number | null;
  comment_count: number;
  comments: Array<{ id: number; body: string; user: User; parent_comment_id: number | null; created_at: string }>
}
interface ToDoList { id: number; name: string; position: number; is_done_state: boolean; to_dos: ToDo[] }
interface Project {
  id: number; name: string; description: string | null; status: string;
  health: string; progress_percent: number; progress_type: string;
  owner: User; linked_rock: { id: number; title: string } | null;
  linked_rock_id: number | null;
  enabled_tools: Record<string, boolean>;
  archived: boolean;
}
interface Membership { id: number; role: string; user: User }
interface StatusUpdate { id: number; health: string; progress_percent: number; summary: string | null; user: User; created_at: string }

interface Props {
  project: Project
  lists: ToDoList[]
  docs: Array<{ id: number; title: string; body: string | null; created_by: User; created_at: string }>
  messages: Array<{ id: number; body: string; user: User; created_at: string }>
  status_updates: StatusUpdate[]
  memberships: Membership[]
  team_links: Array<{ id: number; team_id: number; team_name: string }>
  custom_fields: Array<{ id: number; definition_id: number; name: string; field_type: string; value: any }>
  custom_field_definitions: Array<{ id: number; name: string; field_type: string; options: any[] }>
  org_members: User[]
  teams: Array<{ id: number; name: string }>
  rocks: Array<{ id: number; title: string }>
  open_to_do_id: number | null
  tab: string
  can_edit: boolean
  can_manage: boolean
  can_destroy: boolean
  org_action_items: Array<{ id: number; title: string; owner_id: number }>
  project_topics: Array<{ id: number; title: string; category: string; owner: User | null; team_id: number; created_at: string }>
}

const HEALTH_COLORS: Record<string, string> = {
  on_track: 'bg-green-100 text-green-800',
  at_risk: 'bg-amber-100 text-amber-800',
  off_track: 'bg-red-100 text-red-800'
}

const TABS: Array<{ key: string; label: string; tool: string }> = [
  { key: 'overview', label: 'Overview', tool: '' },
  { key: 'kanban', label: 'Kanban', tool: 'kanban' },
  { key: 'docs', label: 'Docs', tool: 'docs' },
  { key: 'messages', label: 'Messages', tool: 'messages' },
  { key: 'topics', label: 'Topics', tool: 'topics' }
]

export default function ProjectShow(props: Props) {
  const { project, tab, open_to_do_id, lists } = props
  const visibleTabs = TABS.filter((t) => !t.tool || project.enabled_tools[t.tool])
  const activeTab = visibleTabs.find((t) => t.key === tab)?.key || 'overview'
  const openToDo = open_to_do_id ? lists.flatMap((l) => l.to_dos).find((t) => t.id === open_to_do_id) : null

  function changeTab(key: string) {
    router.get(`/projects/${project.id}`, { tab: key }, { preserveState: true, preserveScroll: true })
  }

  return (
    <Layout>
      <ProjectHeader project={project} canEdit={props.can_edit} canDestroy={props.can_destroy} />

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-1">
          {visibleTabs.map((t) => (
            <button
              key={t.key}
              onClick={() => changeTab(t.key)}
              className={`px-4 py-2 text-sm font-medium ${activeTab === t.key ? 'border-b-2 border-[#ED1C24] text-text-primary' : 'text-text-muted'}`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' && <OverviewPanel {...props} />}
      {activeTab === 'kanban' && <KanbanBoard project={project} lists={lists} canEdit={props.can_edit} orgMembers={props.org_members} />}
      {activeTab === 'docs' && <DocsList project={project} docs={props.docs} canEdit={props.can_edit} />}
      {activeTab === 'messages' && <MessagesThread project={project} messages={props.messages} canEdit={props.can_edit} />}
      {activeTab === 'topics' && (
        <div className="bg-white border rounded-lg p-6">
          <h2 className="font-semibold text-text-primary mb-3">Topics from linked teams</h2>
          {props.project_topics.length === 0 ? (
            <p className="text-sm text-text-muted">No topics from this project's linked teams yet.</p>
          ) : (
            <ul className="divide-y">
              {props.project_topics.map((t) => (
                <li key={t.id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <span className="font-medium text-text-primary">{t.title}</span>
                    <span className="ml-2 text-xs text-text-muted">{t.category.replace('_', ' ')}</span>
                  </div>
                  <span className="text-xs text-text-muted">{t.owner?.full_name || '—'}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {openToDo && (
        <ToDoDetail
          project={project}
          toDo={openToDo}
          orgMembers={props.org_members}
          orgActionItems={props.org_action_items}
          canEdit={props.can_edit}
          onClose={() => router.get(`/projects/${project.id}`, { tab: 'kanban' }, { preserveState: true, preserveScroll: true })}
        />
      )}
    </Layout>
  )
}

function ProjectHeader({ project, canEdit, canDestroy }: { project: Project; canEdit: boolean; canDestroy: boolean }) {
  function archive() {
    if (!confirm('Archive this project?')) return
    router.post(`/projects/${project.id}/${project.archived ? 'unarchive' : 'archive'}`)
  }
  function destroy() {
    if (!confirm('Delete this project? This cannot be undone.')) return
    router.delete(`/projects/${project.id}`)
  }
  function saveAsTemplate() {
    const name = prompt('Template name:', `${project.name} Template`)
    if (!name) return
    router.post(`/projects/${project.id}/save_as_template`, { name })
  }
  return (
    <div className="bg-white border rounded-lg p-5 mb-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-text-primary">{project.name}</h1>
          {project.description && <p className="text-sm text-text-muted mt-1">{project.description}</p>}
          <div className="flex items-center gap-3 mt-3">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${HEALTH_COLORS[project.health] || ''}`}>{project.health.replace('_', ' ')}</span>
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-gray-200 rounded overflow-hidden">
                <div className="h-full bg-[#15532E]" style={{ width: `${project.progress_percent}%` }} />
              </div>
              <span className="text-xs text-text-secondary">{project.progress_percent}%</span>
            </div>
            <span className="text-xs text-text-muted">Owner: {project.owner.full_name}</span>
            {project.linked_rock && <span className="text-xs text-[#15532E]">Rock: {project.linked_rock.title}</span>}
            {project.archived && <span className="text-xs text-[#ED1C24] font-semibold">ARCHIVED</span>}
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <button onClick={saveAsTemplate} className="px-3 py-1.5 text-xs border rounded-lg">Save as template</button>
            <button onClick={archive} className="px-3 py-1.5 text-xs border rounded-lg">{project.archived ? 'Unarchive' : 'Archive'}</button>
            {canDestroy && <button onClick={destroy} className="px-3 py-1.5 text-xs text-[#ED1C24] border border-[#ED1C24] rounded-lg">Delete</button>}
          </div>
        )}
      </div>
    </div>
  )
}

function OverviewPanel(props: Props) {
  const { project, status_updates, memberships, team_links, custom_fields, custom_field_definitions, org_members, teams, rocks, can_manage, can_edit } = props
  const { data, setData, post, processing } = useForm({
    health: project.health,
    progress_percent: project.progress_percent,
    summary: ''
  })
  function postStatus(e: React.FormEvent) {
    e.preventDefault()
    post(`/projects/${project.id}/status_updates`, { onSuccess: () => setData('summary', '') })
  }

  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="col-span-2 space-y-4">
        {can_edit && (
          <form onSubmit={postStatus} className="bg-white border rounded-lg p-4 space-y-3">
            <h2 className="font-semibold text-text-primary">Post status update</h2>
            <div className="flex gap-3">
              <select value={data.health} onChange={(e) => setData('health', e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
                <option value="on_track">On track</option>
                <option value="at_risk">At risk</option>
                <option value="off_track">Off track</option>
              </select>
              <input type="number" min={0} max={100} value={data.progress_percent} onChange={(e) => setData('progress_percent', Number(e.target.value))} className="border rounded-lg px-3 py-2 text-sm w-24" />
            </div>
            <textarea rows={2} placeholder="What's the latest?" value={data.summary} onChange={(e) => setData('summary', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
            <button type="submit" disabled={processing} className="px-4 py-2 text-sm text-white rounded-lg" style={{ backgroundColor: '#ED1C24' }}>Post update</button>
          </form>
        )}
        <div className="bg-white border rounded-lg p-4">
          <h2 className="font-semibold text-text-primary mb-3">Recent status updates</h2>
          {status_updates.length === 0 ? (
            <p className="text-sm text-text-muted">No status updates yet.</p>
          ) : (
            <ul className="space-y-3">
              {status_updates.map((s) => (
                <li key={s.id} className="border-b pb-2 last:border-b-0">
                  <div className="flex items-center gap-2 text-xs text-text-muted">
                    <span className="font-medium text-text-secondary">{s.user.full_name}</span>
                    <span className={`px-2 py-0.5 rounded ${HEALTH_COLORS[s.health]}`}>{s.health.replace('_', ' ')}</span>
                    <span>{s.progress_percent}%</span>
                    <span>{new Date(s.created_at).toLocaleString()}</span>
                  </div>
                  {s.summary && <p className="text-sm text-text-primary mt-1 whitespace-pre-wrap">{s.summary}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-text-primary">Members</h2>
            {can_manage && <AddMemberForm projectId={project.id} orgMembers={org_members} existing={memberships.map((m) => m.user.id)} />}
          </div>
          <ul className="space-y-2">
            {sortPeople(memberships).map((m) => (
              <li key={m.id} className="flex items-center justify-between text-sm">
                <span className="text-text-primary">{m.user.full_name}</span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-text-muted">{m.role}</span>
                  {can_manage && m.user.id !== project.owner.id && (
                    <button onClick={() => { if (confirm('Remove member?')) router.delete(`/projects/${project.id}/memberships/${m.id}`) }} className="text-xs text-[#ED1C24]">×</button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-text-primary">Team spaces</h2>
            {can_manage && <AddTeamLinkForm projectId={project.id} teams={teams} existing={team_links.map((tl) => tl.team_id)} />}
          </div>
          <ul className="space-y-1">
            {team_links.map((tl) => (
              <li key={tl.id} className="flex items-center justify-between text-sm">
                <span className="text-text-primary">{tl.team_name}</span>
                {can_manage && <button onClick={() => { if (confirm('Unlink team?')) router.delete(`/projects/${project.id}/team_links/${tl.id}`) }} className="text-xs text-[#ED1C24]">×</button>}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <h2 className="font-semibold text-text-primary mb-2">Custom fields</h2>
          {custom_fields.length === 0 ? (
            <p className="text-xs text-text-muted">No fields applied.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {custom_fields.map((cf) => (
                <li key={cf.id} className="flex justify-between">
                  <span className="text-text-primary">{cf.name}</span>
                  <span className="text-text-muted">{cf.value ? String(cf.value) : '—'}</span>
                </li>
              ))}
            </ul>
          )}
          {can_edit && custom_field_definitions.length > 0 && (
            <AddCustomFieldForm projectId={project.id} definitions={custom_field_definitions} applied={custom_fields.map((cf) => cf.definition_id)} />
          )}
        </div>

        {can_edit && (
          <div className="bg-white border rounded-lg p-4">
            <h2 className="font-semibold text-text-primary mb-2">Link a Rock</h2>
            <select
              defaultValue={project.linked_rock_id || ''}
              onChange={(e) => router.patch(`/projects/${project.id}`, { project: { linked_rock_id: e.target.value || null } })}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">None</option>
              {rocks.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
            </select>
          </div>
        )}
      </div>
    </div>
  )
}

function AddMemberForm({ projectId, orgMembers, existing }: { projectId: number; orgMembers: User[]; existing: number[] }) {
  const [showing, setShowing] = useState(false)
  const candidates = sortPeople(orgMembers.filter((u) => !existing.includes(u.id)))
  if (!showing) return <button onClick={() => setShowing(true)} className="text-xs text-[#15532E]">+ Add</button>
  return (
    <select
      onChange={(e) => { if (e.target.value) { router.post(`/projects/${projectId}/memberships`, { user_id: Number(e.target.value), role: 'collaborator' }); setShowing(false) } }}
      defaultValue=""
      className="text-xs border rounded px-2 py-1"
    >
      <option value="">Pick…</option>
      {candidates.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
    </select>
  )
}

function AddTeamLinkForm({ projectId, teams, existing }: { projectId: number; teams: Array<{ id: number; name: string }>; existing: number[] }) {
  const candidates = teams.filter((t) => !existing.includes(t.id))
  if (candidates.length === 0) return null
  return (
    <select
      onChange={(e) => { if (e.target.value) router.post(`/projects/${projectId}/team_links`, { team_id: Number(e.target.value) }) }}
      defaultValue=""
      className="text-xs border rounded px-2 py-1"
    >
      <option value="">+ Link team…</option>
      {candidates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
    </select>
  )
}

function AddCustomFieldForm({ projectId, definitions, applied }: { projectId: number; definitions: any[]; applied: number[] }) {
  const candidates = definitions.filter((d) => !applied.includes(d.id))
  if (candidates.length === 0) return null
  return (
    <select
      onChange={(e) => { if (e.target.value) router.post(`/projects/${projectId}/custom_fields`, { custom_field_definition_id: Number(e.target.value) }) }}
      defaultValue=""
      className="text-xs border rounded px-2 py-1 mt-2"
    >
      <option value="">+ Add field…</option>
      {candidates.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
    </select>
  )
}

function DocsList({ project, docs, canEdit }: { project: Project; docs: Props['docs']; canEdit: boolean }) {
  const { data, setData, post, processing, reset } = useForm({ title: '', body: '' })
  function create(e: React.FormEvent) {
    e.preventDefault()
    post(`/projects/${project.id}/docs`, { onSuccess: () => reset() })
  }
  return (
    <div className="space-y-4">
      {canEdit && (
        <form onSubmit={create} className="bg-white border rounded-lg p-4 space-y-2">
          <input type="text" required value={data.title} onChange={(e) => setData('title', e.target.value)} placeholder="Doc title" className="w-full border rounded-lg px-3 py-2 text-sm" />
          <textarea rows={3} value={data.body} onChange={(e) => setData('body', e.target.value)} placeholder="Doc body (markdown)" className="w-full border rounded-lg px-3 py-2 text-sm" />
          <button type="submit" disabled={processing} className="px-4 py-2 text-sm text-white rounded-lg" style={{ backgroundColor: '#ED1C24' }}>Create doc</button>
        </form>
      )}
      {docs.length === 0 && <div className="bg-white border rounded-lg p-6 text-sm text-text-muted">No docs yet.</div>}
      {docs.map((d) => (
        <div key={d.id} className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-text-primary">{d.title}</h3>
            <span className="text-xs text-text-muted">{d.created_by.full_name} · {new Date(d.created_at).toLocaleDateString()}</span>
          </div>
          <pre className="text-sm text-text-primary whitespace-pre-wrap mt-2 font-sans">{d.body}</pre>
          {canEdit && <button onClick={() => { if (confirm('Delete doc?')) router.delete(`/projects/${project.id}/docs/${d.id}`) }} className="text-xs text-[#ED1C24] mt-2">Delete</button>}
        </div>
      ))}
    </div>
  )
}

function MessagesThread({ project, messages, canEdit }: { project: Project; messages: Props['messages']; canEdit: boolean }) {
  const { data, setData, post, processing, reset } = useForm({ body: '' })
  function send(e: React.FormEvent) {
    e.preventDefault()
    post(`/projects/${project.id}/messages`, { onSuccess: () => reset() })
  }
  return (
    <div className="space-y-4">
      {canEdit && (
        <form onSubmit={send} className="bg-white border rounded-lg p-4 space-y-2">
          <textarea required rows={2} value={data.body} onChange={(e) => setData('body', e.target.value)} placeholder="Post a message…" className="w-full border rounded-lg px-3 py-2 text-sm" />
          <button type="submit" disabled={processing} className="px-4 py-2 text-sm text-white rounded-lg" style={{ backgroundColor: '#ED1C24' }}>Post</button>
        </form>
      )}
      {messages.length === 0 && <div className="bg-white border rounded-lg p-6 text-sm text-text-muted">No messages yet.</div>}
      {messages.map((m) => (
        <div key={m.id} className="bg-white border rounded-lg p-3">
          <div className="text-xs text-text-muted mb-1">{m.user.full_name} · {new Date(m.created_at).toLocaleString()}</div>
          <p className="text-sm text-text-primary whitespace-pre-wrap">{m.body}</p>
        </div>
      ))}
    </div>
  )
}
